import globalvariables from '@globalvariables'
import { graphql, Link, useStaticQuery } from 'gatsby'
import React from 'react'

import {
  faBars,
  faChevronDown,
  faChevronUp,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import MenuItem from '@components/menuItem'
import { SITE_LINKS } from '../data/menu-items'
import { textTruncate } from '../util/helpers'

import { IconProp } from '@fortawesome/fontawesome-svg-core'
import LogoLight from '../assets/img/logo/cognitive3d-logo-text-white.inline.svg'
import LogoWhite from '../assets/img/logo/cognitive3d-logo-white.inline.svg'
import LogoDark from '../assets/img/logo/cognitive3d-logo.inline.svg'
import { useHasScrolled } from '../util/hooks/useHasScrolled'
import UniversalLink from './universal-link'

const FaBars = () => <FontAwesomeIcon icon={faBars as IconProp} />

type MenuItemType = {
  title?: string
  href?: string
  description?: string
  icon?: IconDefinition | keyof IMenuQueryResult
  iconColor?: string
  iconBackgroundColor?: string
  backgroundColor?: string
  className?: string
  iconImageSharp?: boolean
}

interface MenuItems {
  title: string
  children: MenuItemType[]
}

interface LatesBlogPosts {
  edges: Array<{
    node: {
      fields: {
        slug: string
      }
      frontmatter: {
        title: string
        date: string
      }
    }
  }>
}

interface IMenuQueryResult {
  unityLogo: IImageSharp
  unrealLogo: IImageSharp
  latestBlogPosts: LatesBlogPosts
}

interface IMenuProps {
  logoStyle?: 'light' | 'dark' | 'white'
}

const Header: React.FunctionComponent<IMenuProps> = ({ logoStyle }) => {
  const data: IMenuQueryResult = useStaticQuery(graphql`
    query HeaderQuery {
      unityLogo: file(relativePath: { eq: "supported/unity-logo_black.png" }) {
        childImageSharp {
          gatsbyImageData(width: 30, placeholder: NONE, layout: FIXED)
        }
      }
      unrealLogo: file(relativePath: { eq: "unreal/unreal-logo_black.png" }) {
        childImageSharp {
          gatsbyImageData(width: 30, placeholder: NONE, layout: FIXED)
        }
      }
      latestBlogPosts: allMdx(
        sort: { frontmatter: { date: DESC } }
        filter: {
          fields: {}
          frontmatter: { templateKey: { eq: "blog-post" } }
        }
        limit: 3
      ) {
        edges {
          node {
            fields {
              slug
            }
            frontmatter {
              date
              title
            }
          }
        }
      }
    }
  `)
  const { latestBlogPosts } = data
  const hasScrolled = useHasScrolled()

  const isLogoDark = logoStyle === 'dark' || hasScrolled

  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false)
  const toggleMobileMenuOpen = React.useCallback(
    () => setMobileMenuOpen((menuOpen) => !menuOpen),
    [],
  )
  const mainNavClasses = [
    'nav-top',
    mobileMenuOpen ? 'nav-top-mobile-active' : false,
    mobileMenuOpen ? 'fixed' : false,
  ]
    .filter(Boolean)
    .join(' ')

  const mobileNavClasses = [
    'nav-mobile',
    mobileMenuOpen ? 'nav-mobile--open' : false,
  ]
    .filter(Boolean)
    .join(' ')

  const blogPostsMenuItems = latestBlogPosts.edges.map(({ node }) => ({
    title: textTruncate(node.frontmatter.title, 50),
    href: `/blog/${node.fields.slug}`,
    icon: SITE_LINKS.company.Blog.icon,
    iconColor: 'transparent',
    iconBackgroundColor: 'transparent',
    backgroundColor: SITE_LINKS.company.Blog.backgroundColor,
    className: 'menu-item-blog',
  }))

  const menuItems: MenuItems[] = [
    {
      title: 'Features',
      children: [
        SITE_LINKS.product.SceneExplorer,
        SITE_LINKS.product.SceneViewer,
        SITE_LINKS.product.ObjectExplorer,
        SITE_LINKS.product.Objectives,
        SITE_LINKS.product.ExitPoll,
        SITE_LINKS.product.SupportedEngines,
        SITE_LINKS.product.Unreal,
        SITE_LINKS.product.Unity,
      ],
    },
    {
      title: 'Use Cases',
      children: [
        SITE_LINKS.solution.Training,
        SITE_LINKS.solution.Retail,
        SITE_LINKS.solution.Games,
        SITE_LINKS.solution.Academic,
        SITE_LINKS.company.Resources,
        SITE_LINKS.solution.Enterprise,
      ],
    },
    {
      title: 'Company',
      children: [
        SITE_LINKS.company.About,
        SITE_LINKS.company.Careers,
        SITE_LINKS.company.ProductRoadmap,
        SITE_LINKS.company.PartnerPrograms,
        SITE_LINKS.company.Contact,
        SITE_LINKS.company.PressKit,
        SITE_LINKS.company.Blog,
      ].concat(blogPostsMenuItems),
    },
  ]
  return (
    <React.Fragment>
      <header className={`${hasScrolled ? 'header-sticky' : ''}`}>
        <nav className={mainNavClasses}>
          <div className="container">
            <div className="row">
              <div className="col-xs-4 col-md-3 col-lg-2">
                <div className="logo">
                  <Link to="/">
                    {logoStyle === 'white' || mobileMenuOpen ? (
                      <LogoWhite />
                    ) : isLogoDark ? (
                      <LogoDark />
                    ) : (
                      <LogoLight />
                    )}
                  </Link>
                </div>
              </div>
              <div className="menu end-xs col-xs-8 col-md-9 col-lg-10">
                <div className="row">
                  <ul
                    id="menu-main-nav"
                    className="nav navbar-nav top-nav-menu col-xs-12 row "
                  >
                    {menuItems.map((parentMenu) => {
                      return (
                        <li
                          key={parentMenu.title}
                          className="menu-item menu-item-has-children"
                        >
                          <span className="menu-title">
                            {parentMenu.title}
                            <FontAwesomeIcon
                              icon={faChevronDown as IconProp}
                              size="2x"
                            />
                          </span>
                          <ul className="sub-menu">
                            <li>
                              <FontAwesomeIcon
                                icon={faChevronUp as IconProp}
                                size="2x"
                              />
                            </li>
                            {parentMenu.children.map((childMenuItem, i) => {
                              return (
                                <li
                                  key={i}
                                  className={`menu-item${
                                    childMenuItem.className
                                      ? ` ${childMenuItem.className}`
                                      : ''
                                  }`}
                                  style={{
                                    backgroundColor:
                                      childMenuItem.backgroundColor
                                        ? childMenuItem.backgroundColor
                                        : undefined,
                                  }}
                                >
                                  {childMenuItem.href ? (
                                    <UniversalLink href={childMenuItem.href}>
                                      <MenuItem
                                        title={childMenuItem.title}
                                        description={childMenuItem.description}
                                        icon={
                                          childMenuItem.iconImageSharp
                                            ? (
                                                data[
                                                  childMenuItem.icon as keyof IMenuQueryResult
                                                ] as IImageSharp
                                              ).childImageSharp.gatsbyImageData
                                            : (childMenuItem.icon as IconProp)
                                        }
                                        iconColor={childMenuItem.iconColor}
                                        iconBackgroundColor={
                                          childMenuItem.iconBackgroundColor
                                        }
                                        iconImageSharp={
                                          childMenuItem.iconImageSharp
                                        }
                                      />
                                    </UniversalLink>
                                  ) : (
                                    <div className="no-link">
                                      <MenuItem
                                        title={childMenuItem.title}
                                        description={childMenuItem.description}
                                        icon={
                                          childMenuItem.iconImageSharp
                                            ? (
                                                data[
                                                  childMenuItem.icon as keyof IMenuQueryResult
                                                ] as IImageSharp
                                              ).childImageSharp.gatsbyImageData
                                            : (childMenuItem.icon as IconProp)
                                        }
                                        iconColor={childMenuItem.iconColor}
                                        iconBackgroundColor={
                                          childMenuItem.iconBackgroundColor
                                        }
                                        iconImageSharp={
                                          childMenuItem.iconImageSharp
                                        }
                                      />
                                    </div>
                                  )}
                                </li>
                              )
                            })}
                          </ul>
                        </li>
                      )
                    })}
                    <li className="menu-item  menu-item-has-no-children">
                      <Link to="/pricing/">Pricing</Link>
                    </li>
                    <li className="menu-item">
                      <UniversalLink href="https://docs.cognitive3d.com/">
                        Docs
                      </UniversalLink>
                    </li>
                    <li
                      className="log-in-link menu-item"
                      style={{ marginLeft: 'auto' }}
                    >
                      <a href="https://app.cognitive3d.com/">Sign In</a>
                    </li>
                    <li className="sign-up-link  menu-item">
                      <a
                        href="https://app.cognitive3d.com/get-started"
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-button-green"
                      >
                        {globalvariables.BOOK_DEMO_NAVBAR_BUTTON_TEXT}
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="col-xs-3 col-xs-offset-5 ">
                <div
                  onClick={toggleMobileMenuOpen}
                  className="nav-show-mobile-btn"
                  onKeyPress={toggleMobileMenuOpen}
                  role="button"
                  tabIndex={0}
                >
                  <FaBars />
                </div>
              </div>
            </div>
          </div>
        </nav>
        <nav className={mobileNavClasses}>
          <ul id="menu-main-nav-1" className="nav navbar-nav mobile-nav-menu">
            {menuItems.map((parentMenu) => {
              return (
                <li
                  key={parentMenu.title}
                  className="menu-item menu-item-has-children"
                >
                  <span
                    title={parentMenu.title}
                    className="nav-link"
                    aria-haspopup="true"
                  >
                    {parentMenu.title}
                  </span>
                  <ul role="menu" className="dropdown-menu">
                    {parentMenu.children.map((childMenuItem) => {
                      return (
                        <li
                          key={childMenuItem.href}
                          className={`menu-item${
                            childMenuItem.className
                              ? ` ${childMenuItem.className}`
                              : ''
                          }`}
                        >
                          <a
                            href={childMenuItem.href}
                            title={childMenuItem.title}
                            className="nav-link"
                          >
                            {childMenuItem.title}
                          </a>
                        </li>
                      )
                    })}
                  </ul>
                </li>
              )
            })}
            <li className="menu-item">
              <Link to="/pricing/" className="nav-link">
                Pricing
              </Link>
            </li>
            <li className="menu-item">
              <Link to="/resources/" className="nav-link">
                Resources
              </Link>
            </li>
            <li className="menu-item">
              <UniversalLink
                href="https://docs.cognitive3d.com/"
                className="nav-link"
              >
                Docs
              </UniversalLink>
            </li>
            <li className="log-in-link menu-item">
              <a
                title="Sign In"
                href="https://app.cognitive3d.com/"
                className="nav-link"
              >
                Sign In
              </a>
            </li>
            <li className="sign-up-link menu-item">
              <a
                href="https://app.cognitive3d.com/get-started"
                target="_blank"
                rel="noreferrer"
                className="nav-link"
              >
                {globalvariables.BOOK_DEMO_NAVBAR_BUTTON_TEXT}
              </a>
            </li>
          </ul>
        </nav>
      </header>
    </React.Fragment>
  )
}

export default Header
